import React, { useMemo } from "react";
import { useLocale } from "@/context/LocaleContext";
import { matchPairBroadcast, toBroadcastTeamCode } from "@/lib/broadcastTeamCode";
import { computeEliminatedTeamCodes, computeFinalParticipantCodes } from "@/lib/eliminatedTeams";
import { parseKickoffUtcMs } from "@/lib/kickoffTime";
import LeaderboardMatchdayPicksExport from "@/components/LeaderboardMatchdayPicksExport";
import { MatrixTable, OverviewSection } from "@/components/LeaderboardMatrixTable";

function byKickoffAsc(a, b) {
  const ta = parseKickoffUtcMs(a.kickoff) ?? Infinity;
  const tb = parseKickoffUtcMs(b.kickoff) ?? Infinity;
  if (ta !== tb) return ta - tb;
  return String(a.id ?? "").localeCompare(String(b.id ?? ""));
}

function bdRow(rows, userId, key) {
  const r = rows.find((x) => String(x.user_id) === String(userId));
  return r?.breakdown?.[key] ?? 0;
}

function formatPairBet(b) {
  if (!b) return "–";
  const h = b.home_score;
  const a = b.away_score;
  if (h == null && a == null) return "–";
  return `${h ?? "–"}–${a ?? "–"}`;
}

function topscorerPickShort(fb) {
  if (!fb) return "–";
  const ln = (fb.topscorer_lastname || "").trim();
  if (!ln) return "–";
  if (ln.length <= 4) return ln.toUpperCase();
  return ln.slice(0, 4).toUpperCase();
}

function CrossedOutLabel({ children }) {
  return (
    <span className="relative inline-block leading-none px-px">
      <span>{children}</span>
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full text-[var(--text-dim)]"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <line x1="6" y1="6" x2="94" y2="94" stroke="currentColor" strokeWidth="10" strokeLinecap="round" />
        <line x1="94" y1="6" x2="6" y2="94" stroke="currentColor" strokeWidth="10" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function renderExtraTeamPick(code, eliminated, exemptFromStrike = null) {
  if (!code) return "–";
  const label = toBroadcastTeamCode(code);
  if (eliminated.has(code) && !exemptFromStrike?.has(code)) {
    return <CrossedOutLabel>{label}</CrossedOutLabel>;
  }
  return label;
}

/** Full picks grid under Topplistor → Översikt (not the project match table tab). */
export default function LeaderboardPicksMatrix({ project, picksMatrix, leaderboardRows }) {
  const { t } = useLocale();
  const members = useMemo(() => picksMatrix?.members ?? [], [picksMatrix]);
  const mbU = useMemo(() => picksMatrix?.match_bets_by_user ?? {}, [picksMatrix]);
  const gbU = useMemo(() => picksMatrix?.group_bets_by_user ?? {}, [picksMatrix]);
  const fbU = useMemo(() => picksMatrix?.finals_bets_by_user ?? {}, [picksMatrix]);
  const lbRows = useMemo(() => leaderboardRows || [], [leaderboardRows]);
  const fixtures = useMemo(() => project?.fixtures ?? [], [project]);
  const groups = useMemo(() => project?.groups ?? {}, [project]);

  const groupStageFixtures = useMemo(
    () => fixtures.filter((m) => m.stage === "GROUP").sort(byKickoffAsc),
    [fixtures],
  );

  const koFixtures = useMemo(
    () => fixtures.filter((m) => m.stage && m.stage !== "GROUP").sort(byKickoffAsc),
    [fixtures],
  );

  const eliminatedTeams = useMemo(
    () => computeEliminatedTeamCodes(fixtures, groups),
    [fixtures, groups],
  );

  const finalParticipants = useMemo(
    () => computeFinalParticipantCodes(fixtures),
    [fixtures],
  );

  const groupKeys = useMemo(() => {
    const keys = Object.keys(groups);
    return keys.sort((a, b) => a.localeCompare(b, "sv"));
  }, [groups]);

  const groupRows = useMemo(() => {
    if (!groupKeys.length) return [];
    const slots = [
      { field: "first", placeKey: "standings.place1" },
      { field: "second", placeKey: "standings.place2" },
      { field: "third", placeKey: "standings.place3" },
    ];
    const rows = [];
    for (const g of groupKeys) {
      for (const { field, placeKey } of slots) {
        rows.push([
          `${g} - ${t(placeKey)}`,
          ...members.map((u) => {
            const code = gbU[u.id]?.[g]?.[field];
            return code ? toBroadcastTeamCode(code) : "–";
          }),
        ]);
      }
    }
    return rows;
  }, [groupKeys, members, gbU, t]);

  const matchRowsGroup = useMemo(() => {
    return groupStageFixtures.map((m) => {
      const mid = m.id != null ? String(m.id) : m.id;
      return [
        matchPairBroadcast(m),
        ...members.map((u) => {
          const b = mbU[u.id]?.[mid] ?? mbU[u.id]?.[m.id];
          return formatPairBet(b);
        }),
      ];
    });
  }, [groupStageFixtures, members, mbU]);

  const matchRowsKo = useMemo(() => {
    return koFixtures.map((m) => {
      const mid = m.id != null ? String(m.id) : m.id;
      return [
        matchPairBroadcast(m),
        ...members.map((u) => {
          const b = mbU[u.id]?.[mid] ?? mbU[u.id]?.[m.id];
          return formatPairBet(b);
        }),
      ];
    });
  }, [koFixtures, members, mbU]);

  const extrasRows = useMemo(() => {
    const rows = [];
    rows.push([
      t("lb.pickMatrix.extraFinalistN", { n: 1 }),
      ...members.map((u) => renderExtraTeamPick(fbU[u.id]?.finalists?.[0], eliminatedTeams, finalParticipants)),
    ]);
    rows.push([
      t("lb.pickMatrix.extraFinalistN", { n: 2 }),
      ...members.map((u) => renderExtraTeamPick(fbU[u.id]?.finalists?.[1], eliminatedTeams, finalParticipants)),
    ]);
    rows.push([
      t("lb.pickMatrix.extraWinner"),
      ...members.map((u) => renderExtraTeamPick(fbU[u.id]?.winner, eliminatedTeams)),
    ]);
    rows.push([
      t("lb.pickMatrix.extraTopscorer"),
      ...members.map((u) => topscorerPickShort(fbU[u.id])),
    ]);
    return rows;
  }, [members, fbU, t, eliminatedTeams, finalParticipants]);

  const extrasFooters = useMemo(
    () => [
      { key: "finals", label: "Final" },
      { key: "topscorer", label: "Skyttekung" },
    ],
    [],
  );

  if (!picksMatrix) {
    return <div className="text-center text-[var(--text-dim)] py-12">{t("common.loading")}</div>;
  }

  return (
    <div className="mx-auto w-full max-w-full min-w-0 space-y-8 px-0 pb-2">
      <LeaderboardMatchdayPicksExport project={project} picksMatrix={picksMatrix} />

      {matchRowsGroup.length > 0 && (
        <OverviewSection title={t("lb.pickMatrix.sectionGroupMatches")}>
          <MatrixTable
            rowLabel={t("lb.pickMatrix.colMatch")}
            members={members}
            rows={matchRowsGroup}
            leaderboardRows={lbRows}
            breakdownKey="matches_group"
          />
        </OverviewSection>
      )}

      {groupRows.length > 0 && (
        <OverviewSection title={t("lb.pickMatrix.sectionGroupPicks")}>
          <MatrixTable rowLabel={t("lb.pickMatrix.colGroup")} members={members} rows={groupRows} leaderboardRows={lbRows} breakdownKey="groups" />
        </OverviewSection>
      )}

      {matchRowsKo.length > 0 && (
        <OverviewSection title={t("lb.pickMatrix.sectionKo")}>
          <MatrixTable
            rowLabel={t("lb.pickMatrix.colMatch")}
            members={members}
            rows={matchRowsKo}
            leaderboardRows={lbRows}
            breakdownKey="matches_ko"
          />
        </OverviewSection>
      )}

      <OverviewSection title={t("lb.pickMatrix.sectionExtras")}>
        <MatrixTable
          rowLabel={t("lb.pickMatrix.colPick")}
          members={members}
          rows={extrasRows}
          leaderboardRows={lbRows}
          breakdownKey={null}
          footerRows={extrasFooters}
        />
      </OverviewSection>

      <OverviewSection title="Totalt">
        <MatrixTable
          rowLabel=""
          members={members}
          rows={[
            ["Gruppspel", ...members.map((u) => bdRow(lbRows, u.id, "matches_group"))],
            ["Grupper", ...members.map((u) => bdRow(lbRows, u.id, "groups"))],
            ["Slutspel", ...members.map((u) => bdRow(lbRows, u.id, "matches_ko"))],
            ["Final", ...members.map((u) => bdRow(lbRows, u.id, "finals"))],
            ["Skyttekung", ...members.map((u) => bdRow(lbRows, u.id, "topscorer"))],
            ["Totalt", ...members.map((u) => (lbRows.find((r) => String(r.user_id) === String(u.id))?.score ?? 0))],
          ]}
          leaderboardRows={lbRows}
          breakdownKey={null}
        />
      </OverviewSection>
    </div>
  );
}
